import { useState, useEffect } from 'react'
import { useAtom } from 'jotai'
import { isAdminOrManager } from '../utils/roles'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Stepper,
  Step,
  StepLabel,
  Divider,
  List,
  ListItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AutoAwesome as AIIcon,
  Close as RemoveIcon,
  DragIndicator as DragIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  promptTemplatesApi,
  PromptTemplate,
  SavePromptTemplateInput,
  TemplateSection,
} from '../services/api'
import { useNotification } from '../hooks/useNotification'
import { userAtom } from '../state/auth'
import PageHelpButton from '../components/PageHelpButton'

const DEFAULT_SECTIONS: TemplateSection[] = []

function SortableSectionItem({
  section,
  index,
  onRemove,
}: {
  section: TemplateSection
  index: number
  onRemove: (id: string) => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    background: isDragging ? '#f5f5f5' : undefined,
  }

  return (
    <Box ref={setNodeRef} style={style}>
      {index > 0 && <Divider />}
      <ListItem
        sx={{ px: 2, py: 0.75 }}
        secondaryAction={
          <IconButton size="small" color="error" onClick={() => onRemove(section.id)}>
            <RemoveIcon fontSize="small" />
          </IconButton>
        }
      >
        <Box
          {...attributes}
          {...listeners}
          sx={{ display: 'flex', alignItems: 'center', mr: 1, cursor: 'grab', color: 'text.disabled', touchAction: 'none' }}
        >
          <DragIcon fontSize="small" />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mr: 1, minWidth: 20 }}>
          {index + 1}.
        </Typography>
        <Typography variant="body2" fontWeight={500}>
          {section.name}
        </Typography>
      </ListItem>
    </Box>
  )
}

const STEPS = ['Sections & Order', 'Section Instructions']

interface TemplateFormState {
  id?: string
  name: string
  description: string
  isDefault: boolean
  sections: TemplateSection[]
  createdAt?: string
  createdBy?: string
}

function buildInstructionsFromSections(sections: TemplateSection[]): string {
  const lines = sections.map((s, i) => {
    const base = `${i + 1}. ${s.name}`
    return s.instruction ? `${base}: ${s.instruction}` : base
  })
  return `Generate resume with these sections in order:\n${lines.join('\n')}`
}

export default function ResumeTemplates() {
  const [user] = useAtom(userAtom)
  const [templates, setTemplates] = useState<PromptTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [activeStep, setActiveStep] = useState(0)
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<PromptTemplate | null>(null)
  const [customSectionName, setCustomSectionName] = useState('')
  const [expandedSection, setExpandedSection] = useState<string | false>(false)
  const [form, setForm] = useState<TemplateFormState>({
    name: '',
    description: '',
    isDefault: false,
    sections: DEFAULT_SECTIONS,
  })
  const { showSuccess, showError } = useNotification()

  const canManage = isAdminOrManager(user)

  const loadTemplates = async () => {
    try {
      setLoading(true)
      const data = await promptTemplatesApi.list('resume')
      setTemplates(data)
    } catch {
      showError('Failed to load templates')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadTemplates()
  }, [])

  const openCreateDialog = () => {
    setEditingTemplate(null)
    setActiveStep(0)
    setCustomSectionName('')
    setForm({ name: '', description: '', isDefault: false, sections: [...DEFAULT_SECTIONS] })
    setDialogOpen(true)
  }

  const openEditDialog = (template: PromptTemplate) => {
    setEditingTemplate(template)
    setActiveStep(0)
    setCustomSectionName('')
    const sections = template.sections?.length ? template.sections : [...DEFAULT_SECTIONS]
    setForm({
      id: template.id,
      name: template.name,
      description: template.description || '',
      isDefault: template.isDefault,
      sections,
      createdAt: template.createdAt,
      createdBy: template.createdBy,
    })
    setDialogOpen(true)
  }

  const handleNext = () => {
    if (!form.name.trim()) {
      showError('Template name is required')
      return
    }
    if (form.sections.length === 0) {
      showError('Add at least one section')
      return
    }
    setExpandedSection(form.sections[0]?.id ?? false)
    setActiveStep(1)
  }

  const handleSave = async () => {
    if (!form.name.trim()) {
      showError('Template name is required')
      return
    }
    if (form.sections.length === 0) {
      showError('Add at least one section')
      return
    }
    try {
      setSaving(true)
      const payload: SavePromptTemplateInput = {
        ...(form.id && { id: form.id }),
        name: form.name,
        description: form.description,
        category: 'resume',
        sections: form.sections,
        instructions: buildInstructionsFromSections(form.sections),
        isDefault: form.isDefault,
        ...(form.createdAt && { createdAt: form.createdAt }),
        ...(form.createdBy && { createdBy: form.createdBy }),
      }
      await promptTemplatesApi.save(payload)
      showSuccess(editingTemplate ? 'Template updated' : 'Template created')
      setDialogOpen(false)
      loadTemplates()
    } catch {
      showError('Failed to save template')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await promptTemplatesApi.delete(id)
      showSuccess('Template deleted')
      setDeleteConfirmId(null)
      loadTemplates()
    } catch {
      showError('Failed to delete template')
    }
  }

  // Section management
  const addCustomSection = () => {
    const name = customSectionName.trim()
    if (!name) return
    const id = name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')
    if (form.sections.find((s) => s.id === id)) {
      showError('A section with that name already exists')
      return
    }
    setForm((f) => ({ ...f, sections: [...f.sections, { id, name, instruction: '' }] }))
    setCustomSectionName('')
  }

  const removeSection = (id: string) => {
    setForm((f) => ({ ...f, sections: f.sections.filter((s) => s.id !== id) }))
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = form.sections.findIndex((s) => s.id === active.id)
    const newIndex = form.sections.findIndex((s) => s.id === over.id)
    setForm((f) => ({ ...f, sections: arrayMove(f.sections, oldIndex, newIndex) }))
  }

  const sensors = useSensors(useSensor(PointerSensor))

  const updateSectionInstruction = (id: string, instruction: string) => {
    setForm((f) => ({
      ...f,
      sections: f.sections.map((s) => (s.id === id ? { ...s, instruction } : s)),
    }))
  }

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h5" fontWeight={600}>
              Resume Templates
            </Typography>
            <PageHelpButton articleId="page-resume-templates" />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Create named templates to control which sections appear in a resume and how the AI writes each one.
          </Typography>
        </Box>
        {canManage && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
            New Template
          </Button>
        )}
      </Box>

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Empty state */}
      {!loading && templates.length === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <AIIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No templates yet
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
            Create a template to customize which sections appear in a resume and how the AI writes each one.
          </Typography>
          {canManage && (
            <Button variant="outlined" startIcon={<AddIcon />} onClick={openCreateDialog}>
              Create your first template
            </Button>
          )}
        </Box>
      )}

      {/* Template cards */}
      {!loading && templates.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {templates.map((template) => (
            <Card key={template.id} variant="outlined">
              <CardContent sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {template.name}
                      </Typography>
                      {template.isDefault && (
                        <Chip label="Default" size="small" color="primary" variant="outlined" />
                      )}
                    </Box>
                    {template.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {template.description}
                      </Typography>
                    )}
                    {/* Show sections as chips if available, otherwise show instructions */}
                    {template.sections?.length ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {template.sections.map((s) => (
                          <Chip
                            key={s.id}
                            label={s.name}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.75rem' }}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          color: 'text.primary',
                          bgcolor: 'action.hover',
                          p: 1.5,
                          borderRadius: 1,
                          fontSize: '0.8125rem',
                        }}
                      >
                        {template.instructions}
                      </Typography>
                    )}
                  </Box>
                  {canManage && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEditDialog(template)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteConfirmId(template.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              </CardContent>
              <CardActions sx={{ px: 2, pt: 0, pb: 1.5 }}>
                <Typography variant="caption" color="text.disabled">
                  Created by {template.createdBy} &middot; Last updated{' '}
                  {new Date(template.updatedAt).toLocaleDateString()}
                </Typography>
              </CardActions>
            </Card>
          ))}
        </Box>
      )}

      {/* Create / Edit wizard dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingTemplate ? 'Edit Template' : 'New Resume Template'}
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: '20px !important' }}>
          {/* Step indicator */}
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step 1 — Template info + section builder */}
          {activeStep === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                label="Template Name"
                required
                fullWidth
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Executive Format, ATS Optimized, Tech Resume"
              />
              <TextField
                label="Description"
                fullWidth
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Optional — brief description of this template's purpose"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isDefault}
                    onChange={(e) => setForm((f) => ({ ...f, isDefault: e.target.checked }))}
                  />
                }
                label="Set as default template when generating resumes"
              />

              <Divider />

              <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                  Resume Sections
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Add the sections you want in this resume. You'll set instructions for each one in the next step.
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    size="small"
                    label="Section name"
                    value={customSectionName}
                    onChange={(e) => setCustomSectionName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addCustomSection()}
                    placeholder="e.g. Professional Summary, Work Experience…"
                    fullWidth
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={addCustomSection}
                    disabled={!customSectionName.trim()}
                    sx={{ whiteSpace: 'nowrap', px: 2 }}
                  >
                    Add
                  </Button>
                </Box>

                {form.sections.length === 0 ? (
                  <Box
                    sx={{
                      border: '1px dashed',
                      borderColor: 'divider',
                      borderRadius: 1,
                      py: 3,
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="body2" color="text.disabled">
                      No sections yet — type a name above and click Add
                    </Typography>
                  </Box>
                ) : (
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={form.sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                      <List
                        disablePadding
                        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                      >
                        {form.sections.map((section, index) => (
                          <SortableSectionItem
                            key={section.id}
                            section={section}
                            index={index}
                            onRemove={removeSection}
                          />
                        ))}
                      </List>
                    </SortableContext>
                  </DndContext>
                )}
              </Box>
            </Box>
          )}

          {/* Step 2 — Per-section instructions */}
          {activeStep === 1 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Add optional guidance for each section. Leave blank to use the AI's default approach.
              </Typography>
              {form.sections.map((section, index) => (
                <Accordion
                  key={section.id}
                  expanded={expandedSection === section.id}
                  onChange={(_, isExpanded) => setExpandedSection(isExpanded ? section.id : false)}
                  disableGutters
                  elevation={0}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:not(:last-child)': { borderBottom: 0 },
                    '&:first-of-type': { borderRadius: '4px 4px 0 0' },
                    '&:last-of-type': { borderRadius: '0 0 4px 4px' },
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', pr: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 20 }}>
                        {index + 1}.
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {section.name}
                      </Typography>
                      {section.instruction && (
                        <Chip label="Has instructions" size="small" color="primary" variant="outlined" sx={{ ml: 'auto', fontSize: '0.7rem' }} />
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, pb: 2 }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      size="small"
                      placeholder="e.g. Tone, length, what to emphasize, how many bullet points…"
                      value={section.instruction}
                      onChange={(e) => updateSectionInstruction(section.id, e.target.value)}
                      autoFocus={expandedSection === section.id}
                    />
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          {activeStep === 1 && (
            <Button onClick={() => setActiveStep(0)} disabled={saving} sx={{ mr: 1 }}>
              Back
            </Button>
          )}
          {activeStep === 0 ? (
            <Button variant="contained" onClick={handleNext}>
              Next: Add Instructions
            </Button>
          ) : (
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? <CircularProgress size={18} /> : editingTemplate ? 'Save Changes' : 'Create Template'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)} maxWidth="xs">
        <DialogTitle>Delete Template?</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            This template will be removed from the dropdown. Any resumes already generated with it are unaffected.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
